/**
 * The compiled NPC files of a world version (ADR-136 decision 2.4): the server part, `server/npcs.json`, holding each
 * template the world's `npc` markers name with its `appearance` and each placement with its defaults resolved; and the
 * public look table, `npc-looks.json`, one name, character sheet and plate colour per template in template id order,
 * which is all a page learns of an NPC (its `appearance` indexes it). Readers of both are here for the map server and the
 * page. Pure.
 */

import { type Dialogue, validateDialogue } from "../content/dialogue.ts";
import type { LineText } from "../content/line.ts";
import { type NpcCell, type NpcFacing, type NpcFields, type NpcMovement, type NpcPathEnds, readNpcFields } from "./marker.ts";
import { type NpcPlate, type NpcTemperament, npcPolicy } from "./policy.ts";
import { NPC_SCALES, type NpcScale, type NpcTemplate, validateNpcTemplate } from "./template.ts";

const MAX_APPEARANCE = 65_535;
const SOUND_FOLDER = /^[a-z0-9-]+$/;
const PLATES: readonly NpcPlate[] = ["white", "gold", "red", "green"];
const PLACEMENT_KEYS = ["template", "dialogue", "facing", "movement", "area", "path", "pathEnds", "roamTiles", "temperament", "level", "respawn"] as const;

/**
 * A template as the server part holds it: the authored template and its index in the look table.
 */
export type CompiledNpcTemplate = NpcTemplate & { readonly appearance: number };

/**
 * One placed NPC as the server part holds it: the marker's id, map and feet point in tiles, and its fields with the
 * dialogue, temperament and level resolved against the template (the marker's own value, else the template's).
 * `dialogue` is absent when neither names one.
 */
export interface NpcPlacement {
  readonly id: string;
  readonly map: string;
  readonly x: number;
  readonly y: number;
  readonly template: string;
  readonly dialogue?: string;
  readonly facing: NpcFacing;
  readonly movement: NpcMovement;
  readonly area: readonly NpcCell[];
  readonly path: readonly NpcCell[];
  readonly pathEnds: NpcPathEnds;
  readonly roamTiles: number;
  readonly temperament: NpcTemperament;
  readonly level: number;
  readonly respawn: number;
}

/**
 * One entry of a version's look table as `npc-looks.json` holds it.
 */
export interface NpcLook {
  readonly name: string;
  readonly sheet: string;
  readonly plate: NpcPlate;
  /** The size the page draws the sheet at (`NPC_SCALES`); absent for 1. */
  readonly scale?: NpcScale;
  /** The sound folder under `sfx/npcs/` (the template's `sound`); absent for an NPC that makes none. */
  readonly sound?: string;
}

/**
 * The templates a version holds in look table order, template id order, each with its index there.
 *
 * @param templates The templates the world's markers name, each once.
 * @returns The compiled templates.
 */
export function compileNpcTemplates(templates: readonly NpcTemplate[]): CompiledNpcTemplate[] {
  return [...templates].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)).map((template, appearance) => ({ ...template, appearance }));
}

/**
 * The plain text of a template's name, which `validateNpcTemplate` holds to plain text.
 *
 * @param text The name line's text.
 * @returns The text, or its `other` form for a plural value a template cannot hold.
 */
export function plainName(text: LineText): string {
  return typeof text === "string" ? text : (text.other ?? "");
}

/**
 * The public look table of a version: one name, sheet, plate colour, drawn size and sound folder per template, in template id order,
 * so a template's `appearance` in `compileNpcTemplates` indexes it. The plate is the template's temperament's; a
 * marker that overrides the temperament does not change how the page draws the plate of its look.
 *
 * @param templates The templates the world's markers name, each once.
 * @returns The table as `npc-looks.json` holds it.
 */
export function npcLooksOf(templates: readonly NpcTemplate[]): { readonly looks: readonly NpcLook[] } {
  return { looks: compileNpcTemplates(templates).map((template) => ({ name: plainName(template.name.text), sheet: template.look, plate: npcPolicy(template.temperament).plate, ...(template.scale === undefined ? {} : { scale: template.scale }), ...(template.sound === undefined ? {} : { sound: template.sound }) })) };
}

/**
 * One marker's placement, its choices resolved against its template.
 *
 * @param map The marker's map id.
 * @param id The marker's id, the NPC's definition id.
 * @param x The marker's x in tiles.
 * @param y The marker's y in tiles.
 * @param fields The marker's fields as `readNpcFields` read them.
 * @param template The template the marker names.
 * @returns The placement.
 */
export function npcPlacementOf(map: string, id: string, x: number, y: number, fields: NpcFields, template: NpcTemplate): NpcPlacement {
  const dialogue = fields.dialogue ?? template.dialogue;
  return {
    id,
    map,
    x,
    y,
    template: fields.template,
    ...(dialogue === undefined ? {} : { dialogue }),
    facing: fields.facing,
    movement: fields.movement,
    area: fields.area,
    path: fields.path,
    pathEnds: fields.pathEnds,
    roamTiles: fields.roamTiles,
    temperament: fields.temperament ?? template.temperament,
    level: fields.level ?? template.level,
    respawn: fields.respawn,
  };
}

/**
 * Whether a value is a plain object.
 *
 * @param value Anything.
 * @returns True for an object that is neither null nor an array.
 */
function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Reads a version's `npc-looks.json`.
 *
 * @param value The parsed file.
 * @returns The looks, indexed by an NPC's `appearance`.
 * @throws Error when the file is not a list of names, sheets, plates and template ids.
 */
export function parseNpcLooks(value: unknown): NpcLook[] {
  const looks = isRecord(value) ? value["looks"] : undefined;
  if (!Array.isArray(looks)) {
    throw new Error("npc-looks.json must hold a looks list");
  }
  return looks.map((entry: unknown, i): NpcLook => {
    if (!isRecord(entry) || typeof entry["name"] !== "string" || typeof entry["sheet"] !== "string") {
      throw new Error(`npc-looks.json: looks[${i}] must hold a name, a sheet and a plate`);
    }
    const plate = entry["plate"];
    if (!PLATES.includes(plate as NpcPlate)) {
      throw new Error(`npc-looks.json: looks[${i}]'s plate must be ${PLATES.slice(0, -1).join(", ")} or ${PLATES[PLATES.length - 1] as string}`);
    }
    const scale = entry["scale"];
    if (scale !== undefined && !(NPC_SCALES as readonly unknown[]).includes(scale)) {
      throw new Error(`npc-looks.json: looks[${i}]'s scale must be ${NPC_SCALES.join(", ")}`);
    }
    const sound = entry["sound"];
    if (sound !== undefined && (typeof sound !== "string" || !SOUND_FOLDER.test(sound))) {
      throw new Error(`npc-looks.json: looks[${i}]'s sound must name a sound folder in lower-case letters, digits and -`);
    }
    return { name: entry["name"], sheet: entry["sheet"], plate: plate as NpcPlate, ...(scale === undefined ? {} : { scale: scale as NpcScale }), ...(sound === undefined ? {} : { sound }) };
  });
}

/**
 * Reads one placement of the server part, checking its fields as the marker's were checked.
 *
 * @param entry The parsed entry.
 * @param i Its index, for the error.
 * @param known The ids of the templates the file holds.
 * @returns The placement.
 * @throws Error naming the placement when it is malformed or names a template the file lacks.
 */
function readPlacement(entry: unknown, i: number, known: ReadonlySet<string>): NpcPlacement {
  if (!isRecord(entry) || typeof entry["id"] !== "string" || typeof entry["map"] !== "string" || typeof entry["x"] !== "number" || typeof entry["y"] !== "number") {
    throw new Error(`npcs.json: npcs[${i}] must hold an id, a map and a position`);
  }
  const id = entry["id"];
  const subset: { [key: string]: never } = {};
  for (const key of PLACEMENT_KEYS) {
    if (entry[key] !== undefined) {
      subset[key] = entry[key] as never;
    }
  }
  const fields = readNpcFields(subset);
  if (!fields.ok) {
    throw new Error(`npcs.json: npc ${id} ${fields.errors.join("; ")}`);
  }
  if (!known.has(fields.npc.template)) {
    throw new Error(`npcs.json: npc ${id} names the template ${fields.npc.template}, which the file does not hold`);
  }
  const { temperament, level } = entry;
  if (typeof temperament !== "string" || typeof level !== "number") {
    throw new Error(`npcs.json: npc ${id} must hold its resolved temperament and level`);
  }
  return {
    id,
    map: entry["map"],
    x: entry["x"],
    y: entry["y"],
    template: fields.npc.template,
    ...(fields.npc.dialogue === undefined ? {} : { dialogue: fields.npc.dialogue }),
    facing: fields.npc.facing,
    movement: fields.npc.movement,
    area: fields.npc.area,
    path: fields.npc.path,
    pathEnds: fields.npc.pathEnds,
    roamTiles: fields.npc.roamTiles,
    temperament: temperament as NpcTemperament,
    level,
    respawn: fields.npc.respawn,
  };
}

/**
 * Reads a version's `server/npcs.json`, checking each template as its source was checked, its appearance, and each
 * placement as its marker was.
 *
 * @param value The parsed file.
 * @param topPercent The health percent of the creature table's last level (`topHealthPercent`).
 * @returns The templates and the placements in the file's order.
 * @throws Error naming the template or the marker and the field when one is malformed.
 */
export function parseCompiledNpcs(value: unknown, topPercent: number): { readonly templates: CompiledNpcTemplate[]; readonly npcs: NpcPlacement[] } {
  const templates = isRecord(value) ? value["templates"] : undefined;
  const npcs = isRecord(value) ? value["npcs"] : undefined;
  if (!Array.isArray(templates)) {
    throw new Error("npcs.json must hold a templates list");
  }
  if (!Array.isArray(npcs)) {
    throw new Error("npcs.json must hold an npcs list");
  }
  const compiled = templates.map((entry: unknown, i): CompiledNpcTemplate => {
    if (!isRecord(entry)) {
      throw new Error(`npcs.json: templates[${i}] must be a JSON object`);
    }
    const { appearance, ...rest } = entry;
    const fileId = typeof rest["id"] === "string" ? rest["id"] : `templates[${i}]`;
    const verdict = validateNpcTemplate(rest, fileId, topPercent);
    if (!verdict.ok) {
      throw new Error(verdict.errors.join("; "));
    }
    if (typeof appearance !== "number" || !Number.isInteger(appearance) || appearance < 0 || appearance > MAX_APPEARANCE) {
      throw new Error(`npcs.json: ${fileId}'s appearance must be an integer from 0 to ${MAX_APPEARANCE}`);
    }
    return { ...verdict.template, appearance };
  });
  const known = new Set(compiled.map((template) => template.id));
  return { templates: compiled, npcs: npcs.map((entry: unknown, i) => readPlacement(entry, i, known)) };
}

/**
 * Reads a version's `server/dialogues.json`, checking each conversation as its source file was checked.
 *
 * @param value The parsed file.
 * @returns The dialogues in the file's order.
 * @throws Error naming the dialogue and the node and field when one is malformed.
 */
export function parseCompiledDialogues(value: unknown): Dialogue[] {
  const dialogues = isRecord(value) ? value["dialogues"] : undefined;
  if (!Array.isArray(dialogues)) {
    throw new Error("dialogues.json must hold a dialogues list");
  }
  return dialogues.map((entry: unknown, i): Dialogue => {
    const fileId = isRecord(entry) && typeof entry["id"] === "string" ? entry["id"] : `dialogues[${i}]`;
    const verdict = validateDialogue(entry, fileId);
    if (!verdict.ok) {
      throw new Error(`dialogues.json: ${verdict.problems.map((problem) => `${problem.file}${problem.node === null ? "" : ` node ${problem.node}`} ${problem.field}: ${problem.message}`).join("; ")}`);
    }
    return verdict.dialogue;
  });
}
